/**
 * ReviewQueue - Flagged transaction review component
 *
 * Displays transactions that need human review during bulk import.
 * Shows flag types, suggestions, and batch action controls.
 */

import { useState } from "react";
import {
  AlertTriangle,
  CircleAlert,
  Sparkles,
  Check,
  Edit,
  X,
  ChevronRight,
  CheckCheck,
  Loader2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

export type FlagType = "lowConfidence" | "newPattern" | "anomaly";

export interface FlaggedTransaction {
  id: number;
  date: number;
  description: string;
  amount: number; // cents
  suggestedVendor?: string;
  suggestedCategory?: string;
  suggestedAccountCode?: string;
  suggestedVatRate?: number;
  confidence: number;
  flags: FlagType[];
  similarCount?: number;
}

interface ReviewQueueProps {
  transactions: FlaggedTransaction[];
  onApprove: (id: number) => void;
  onApproveAll: () => void;
  onEdit: (id: number) => void;
  onSkip: (id: number) => void;
  onApplyToSimilar: (id: number) => void;
  isLoading?: boolean;
}

// Format amount in Swiss style (CHF 1'234.56)
function formatSwissCurrency(cents: number): string {
  const amount = Math.abs(cents) / 100;
  const formatted = amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = cents < 0 ? "-" : "";
  return `${sign}CHF ${formatted}`;
}

// Format date
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Flag icon component
function FlagIcon({ flag }: { flag: FlagType }) {
  switch (flag) {
    case "lowConfidence":
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-warning/20">
                <CircleAlert className="w-3.5 h-3.5 text-warning" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Low confidence - uncertain categorization</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "newPattern":
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0.5 bg-info/10 text-info border-[var(--info)]/30"
              >
                NEW
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>New pattern - first time seeing this vendor/pattern</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "anomaly":
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-destructive/20">
                <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Anomaly - unusual amount or potential duplicate</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    default:
      return null;
  }
}

// Confidence bar with color coding
function ConfidenceBar({ confidence }: { confidence: number }) {
  const getColor = () => {
    if (confidence < 50) return "bg-destructive";
    if (confidence < 70) return "bg-warning";
    return "bg-success";
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", getColor())}
          style={{ width: `${confidence}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-10 text-right">
        {confidence}%
      </span>
    </div>
  );
}

// Single transaction card
function TransactionCard({
  transaction,
  onApprove,
  onEdit,
  onSkip,
  onApplyToSimilar,
}: {
  transaction: FlaggedTransaction;
  onApprove: () => void;
  onEdit: () => void;
  onSkip: () => void;
  onApplyToSimilar: () => void;
}) {
  return (
    <Card className="relative">
      {/* Flags in top-right corner */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        {transaction.flags.map(flag => (
          <FlagIcon key={flag} flag={flag} />
        ))}
      </div>

      <CardHeader className="pb-2">
        <div className="flex items-start gap-3 pr-20">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{formatDate(transaction.date)}</span>
              <span className="font-mono font-semibold text-foreground">
                {formatSwissCurrency(transaction.amount)}
              </span>
            </div>
            <p
              className="mt-1 text-sm font-medium truncate"
              title={transaction.description}
            >
              {transaction.description}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 pb-3">
        {/* Suggestions */}
        <div className="space-y-2 bg-muted/50 rounded-md p-3">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5" />
            Suggestions
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {transaction.suggestedVendor && (
              <>
                <span className="text-muted-foreground">Vendor:</span>
                <span className="font-medium truncate">
                  {transaction.suggestedVendor}
                </span>
              </>
            )}
            {transaction.suggestedCategory && (
              <>
                <span className="text-muted-foreground">Category:</span>
                <span className="font-medium truncate">
                  {transaction.suggestedCategory}
                </span>
              </>
            )}
            {transaction.suggestedAccountCode && (
              <>
                <span className="text-muted-foreground">Account:</span>
                <span className="font-mono">
                  {transaction.suggestedAccountCode}
                </span>
              </>
            )}
            {transaction.suggestedVatRate !== undefined && (
              <>
                <span className="text-muted-foreground">VAT:</span>
                <span>{(transaction.suggestedVatRate / 100).toFixed(1)}%</span>
              </>
            )}
          </div>
          <div className="pt-1">
            <ConfidenceBar confidence={transaction.confidence} />
          </div>
        </div>

        {/* Similar transactions link */}
        {transaction.similarCount && transaction.similarCount > 0 && (
          <button
            onClick={onApplyToSimilar}
            className="mt-2 flex items-center gap-1 text-xs text-[var(--primary)] hover:underline"
          >
            <span>Apply to {transaction.similarCount} similar</span>
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </CardContent>

      <CardFooter className="pt-0 gap-2">
        <Button size="sm" onClick={onApprove} className="flex-1">
          <Check className="w-4 h-4 mr-1" />
          Approve
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onSkip}>
          <X className="w-4 h-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}

export function ReviewQueue({
  transactions,
  onApprove,
  onApproveAll,
  onEdit,
  onSkip,
  onApplyToSimilar,
  isLoading = false,
}: ReviewQueueProps) {
  if (transactions.length === 0 && !isLoading) {
    return (
      <div className="text-center py-12">
        <CheckCheck className="w-12 h-12 mx-auto text-success mb-3" />
        <h3 className="text-lg font-semibold">All Clear!</h3>
        <p className="text-muted-foreground text-sm mt-1">
          No transactions need review.
        </p>
      </div>
    );
  }

  // Count flags
  const flagCounts = {
    lowConfidence: transactions.filter(t => t.flags.includes("lowConfidence"))
      .length,
    newPattern: transactions.filter(t => t.flags.includes("newPattern")).length,
    anomaly: transactions.filter(t => t.flags.includes("anomaly")).length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">
            Review Queue
            <Badge variant="secondary" className="ml-2">
              {transactions.length}
            </Badge>
          </h3>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {flagCounts.lowConfidence > 0 && (
              <span className="flex items-center gap-1">
                <CircleAlert className="w-3 h-3 text-warning" />
                {flagCounts.lowConfidence} low confidence
              </span>
            )}
            {flagCounts.newPattern > 0 && (
              <span className="flex items-center gap-1">
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  NEW
                </Badge>
                {flagCounts.newPattern} new patterns
              </span>
            )}
            {flagCounts.anomaly > 0 && (
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-destructive" />
                {flagCounts.anomaly} anomalies
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={onApproveAll}
          disabled={isLoading || transactions.length === 0}
        >
          <CheckCheck className="w-4 h-4 mr-2" />
          Approve All
        </Button>
      </div>

      {/* Transaction cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {transactions.map(txn => (
          <TransactionCard
            key={txn.id}
            transaction={txn}
            onApprove={() => onApprove(txn.id)}
            onEdit={() => onEdit(txn.id)}
            onSkip={() => onSkip(txn.id)}
            onApplyToSimilar={() => onApplyToSimilar(txn.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default ReviewQueue;

/**
 * ReviewQueueContainer - Self-contained version that fetches its own data
 *
 * Use this when you need a standalone review queue for a company.
 */
export interface ReviewQueueContainerProps {
  companyId: number;
  batchId?: number;
  transactionIds?: number[];
  onComplete?: () => void;
}

export function ReviewQueueContainer({
  companyId,
  batchId,
  transactionIds,
  onComplete,
}: ReviewQueueContainerProps) {
  // Fetch flagged transactions
  const { data, isLoading, refetch } =
    trpc.transactionIntelligence.batchReview.useQuery({
      companyId,
      batchId,
      transactionIds,
    });

  // Use the fetched data directly, or fall back to empty array
  const localTransactions = data?.flagged ?? [];

  // Approve mutation
  const approveMutation = trpc.transactionIntelligence.bulkApprove.useMutation({
    onSuccess: result => {
      toast.success(`Approved ${result.approved} transaction(s)`);
      refetch();
    },
    onError: error => {
      toast.error(`Failed to approve: ${error.message}`);
    },
  });

  // Skip mutation
  const skipMutation =
    trpc.transactionIntelligence.skipTransactions.useMutation({
      onSuccess: result => {
        toast.success(`Skipped ${result.skipped} transaction(s)`);
        refetch();
      },
      onError: error => {
        toast.error(`Failed to skip: ${error.message}`);
      },
    });

  const handleApprove = (id: number) => {
    const txn = localTransactions.find(t => t.id === id);
    approveMutation.mutate({
      transactionIds: [id],
      companyId,
      category: txn?.suggestedCategory,
      accountCode: txn?.suggestedAccountCode,
      vatRate: txn?.suggestedVatRate,
    });
  };

  const handleApproveAll = () => {
    const ids = localTransactions.map(t => t.id);
    if (ids.length === 0) {
      onComplete?.();
      return;
    }
    approveMutation.mutate({
      transactionIds: ids,
      companyId,
      applyToSimilar: true,
    });
  };

  const handleSkip = (id: number) => {
    skipMutation.mutate({ transactionIds: [id], companyId });
  };

  const handleEdit = (id: number) => {
    // Navigate to transaction edit page
    window.location.href = `/transactions?edit=${id}`;
  };

  const handleApplyToSimilar = (id: number) => {
    const txn = localTransactions.find(t => t.id === id);
    if (!txn) return;

    // Find similar transaction IDs
    const similarIds = localTransactions
      .filter(t => t.id !== id && t.suggestedCategory === txn.suggestedCategory)
      .map(t => t.id);

    if (similarIds.length === 0) {
      handleApprove(id);
      return;
    }

    approveMutation.mutate({
      transactionIds: [id, ...similarIds],
      companyId,
      category: txn.suggestedCategory,
      accountCode: txn.suggestedAccountCode,
      vatRate: txn.suggestedVatRate,
      applyToSimilar: true,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Empty state - call onComplete if all done
  if (localTransactions.length === 0 && !isLoading) {
    return (
      <div className="text-center py-12">
        <CheckCheck className="w-12 h-12 mx-auto text-success mb-3" />
        <h3 className="text-lg font-semibold">All Clear!</h3>
        <p className="text-muted-foreground text-sm mt-1">
          No transactions need review.
        </p>
        {onComplete && (
          <Button onClick={onComplete} className="mt-4">
            Continue
          </Button>
        )}
      </div>
    );
  }

  return (
    <ReviewQueue
      transactions={localTransactions}
      onApprove={handleApprove}
      onApproveAll={handleApproveAll}
      onEdit={handleEdit}
      onSkip={handleSkip}
      onApplyToSimilar={handleApplyToSimilar}
      isLoading={approveMutation.isPending || skipMutation.isPending}
    />
  );
}
